import { useCallback, useEffect, useRef, useState } from 'react'
import type { KeyboardEvent } from 'react'
import { useReducedMotion } from 'framer-motion'

const DESKTOP_MIN = 720

/**
 * State for the coverflow carousel: active index, autoplay (paused on hover,
 * focus or when the lightbox is open) and stage measurements.
 */
export function useCoverflow(count: number, interval = 5000) {
  const [index, setIndex] = useState(0)
  const [hovered, setHovered] = useState(false)
  const [focused, setFocused] = useState(false)
  const [lightbox, setLightbox] = useState<number | null>(null)
  const [stageWidth, setStageWidth] = useState(0)
  const stageRef = useRef<HTMLDivElement>(null)
  const panned = useRef(false)
  const reduceMotion = useReducedMotion()

  const next = useCallback(() => setIndex((i) => (i + 1) % count), [count])
  const prev = useCallback(
    () => setIndex((i) => (i - 1 + count) % count),
    [count],
  )

  const paused = hovered || focused || lightbox !== null || !!reduceMotion

  // Restart the timer after every change so manual navigation gets a full 5s.
  useEffect(() => {
    if (paused) return
    const id = window.setTimeout(next, interval)
    return () => window.clearTimeout(id)
  }, [paused, next, interval, index])

  useEffect(() => {
    const el = stageRef.current
    if (!el) return
    const ro = new ResizeObserver(([entry]) =>
      setStageWidth(entry.contentRect.width),
    )
    ro.observe(el)
    return () => ro.disconnect()
  }, [])

  const isDesktop = stageWidth >= DESKTOP_MIN
  const slideWidth = isDesktop
    ? Math.min(400, stageWidth * 0.36)
    : Math.min(360, stageWidth * 0.78)
  const spacing = isDesktop ? slideWidth * 0.93 : slideWidth + 16

  /** Shortest signed distance from the active slide, wrapping around. */
  const offsetOf = (i: number) => {
    let d = i - index
    if (d > count / 2) d -= count
    if (d < -count / 2) d += count
    return d
  }

  const onPanStart = () => {
    panned.current = true
  }
  const onPanEnd = (_: unknown, info: { offset: { x: number } }) => {
    if (info.offset.x < -40) next()
    else if (info.offset.x > 40) prev()
  }
  /** Returns true if the click was really the end of a swipe. */
  const consumePan = () => {
    if (!panned.current) return false
    panned.current = false
    return true
  }

  const onKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'ArrowRight') {
      e.preventDefault()
      next()
    } else if (e.key === 'ArrowLeft') {
      e.preventDefault()
      prev()
    }
  }

  return {
    index,
    setIndex,
    next,
    prev,
    lightbox,
    setLightbox,
    setHovered,
    setFocused,
    stageRef,
    isDesktop,
    slideWidth,
    spacing,
    offsetOf,
    onPanStart,
    onPanEnd,
    consumePan,
    onKeyDown,
    reduceMotion: !!reduceMotion,
  }
}
